import type { GameStateSnapshot, SnakeState, Bubble, Projectile, BubbleType, SnakeEgg, Headstone } from '@twitch-snake/common';

const BUBBLE_COLORS: Record<BubbleType, string> = {
  food: '#4caf50',
  rare_food: '#FFD700',
  speed_up: '#ff9800',
  slow: '#2196f3',
  kill_bonus: '#e91e63',
  konami: '#ff00ff', // Placeholder - rainbow drawn dynamically
};

// Colors for multiplayer - distinct colors for up to 4 players
const PLAYER_COLORS = [
  '#9147ff', // Twitch purple (player 1)
  '#00bcd4', // Cyan (player 2)
  '#ffeb3b', // Yellow (player 3)
  '#ff5722', // Deep orange (player 4)
];

export class GameRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cellWidth = 0;
  private cellHeight = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(state: GameStateSnapshot): void {
    const { gridWidth, gridHeight } = state;
    
    // Calculate cell size
    this.cellWidth = this.canvas.width / gridWidth;
    this.cellHeight = this.canvas.height / gridHeight;

    // Clear canvas
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw grid (subtle)
    this.drawGrid(gridWidth, gridHeight);

    // Draw bubbles
    for (const bubble of state.bubbles) {
      try {
        this.drawBubble(bubble);
      } catch (e) {
        console.error('Bubble render error:', bubble.type, e);
      }
    }

    // Draw snake eggs
    for (const egg of (state.eggs || [])) {
      this.drawEgg(egg);
    }

    // Draw headstones
    for (const headstone of (state.headstones || [])) {
      this.drawHeadstone(headstone);
    }

    // Draw projectiles
    for (const projectile of state.projectiles) {
      this.drawProjectile(projectile);
    }

    // Draw enemy snakes (differentiate babies)
    for (const enemy of state.enemySnakes) {
      if (enemy.isBaby) {
        this.drawSnake(enemy, '#ff6f00'); // Orange for baby snakes
      } else {
        this.drawSnake(enemy, '#f44336');
      }
    }

    // Draw all player snakes with different colors
    const playerSnakes = state.playerSnakes || (state.playerSnake ? [state.playerSnake] : []);
    playerSnakes.forEach((snake, index) => {
      const color = PLAYER_COLORS[index % PLAYER_COLORS.length];
      if (snake.isRainbow) {
        this.drawRainbowSnake(snake);
      } else {
        this.drawSnake(snake, color);
      }
      
      // Draw player label above snake
      if (snake.owner && snake.segments.length > 0) {
        this.drawPlayerLabel(snake, snake.owner.displayName, color);
      }
    });
  }

  private drawGrid(width: number, height: number): void {
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    this.ctx.lineWidth = 1;

    for (let x = 0; x <= width; x++) {
      this.ctx.beginPath();
      this.ctx.moveTo(x * this.cellWidth, 0);
      this.ctx.lineTo(x * this.cellWidth, this.canvas.height);
      this.ctx.stroke();
    }

    for (let y = 0; y <= height; y++) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y * this.cellHeight);
      this.ctx.lineTo(this.canvas.width, y * this.cellHeight);
      this.ctx.stroke();
    }
  }

  private drawSnake(snake: SnakeState, color: string): void {
    if (!snake.alive || snake.segments.length === 0) return;

    const segments = snake.segments;

    // Draw body segments
    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i];
      const isHead = i === 0;
      
      const x = segment.x * this.cellWidth;
      const y = segment.y * this.cellHeight;
      const baseRadius = Math.min(this.cellWidth, this.cellHeight) * 0.45; // Increased from 0.4
      const radius = isHead ? baseRadius * 1.15 : baseRadius;

      // Add glow effect
      this.ctx.shadowColor = color;
      this.ctx.shadowBlur = isHead ? 15 : 8;
      
      this.ctx.beginPath();
      this.ctx.arc(
        x + this.cellWidth / 2,
        y + this.cellHeight / 2,
        radius,
        0,
        Math.PI * 2
      );
      
      // Gradient from head to tail
      const alpha = 0.6 + (0.4 * (segments.length - i) / segments.length);
      this.ctx.fillStyle = color;
      this.ctx.globalAlpha = alpha;
      this.ctx.fill();
      this.ctx.globalAlpha = 1;
      this.ctx.shadowBlur = 0;

      // Eyes on head
      if (isHead) {
        this.drawEyes(segment.x, segment.y, snake.direction);
      }
    }
  }

  private drawEyes(x: number, y: number, direction: string): void {
    const cx = x * this.cellWidth + this.cellWidth / 2;
    const cy = y * this.cellHeight + this.cellHeight / 2;
    const eyeOffset = this.cellWidth * 0.15;
    const eyeRadius = this.cellWidth * 0.08;

    let leftEye = { x: cx - eyeOffset, y: cy - eyeOffset };
    let rightEye = { x: cx + eyeOffset, y: cy - eyeOffset };

    switch (direction) {
      case 'down':
        leftEye = { x: cx - eyeOffset, y: cy + eyeOffset };
        rightEye = { x: cx + eyeOffset, y: cy + eyeOffset };
        break;
      case 'left':
        leftEye = { x: cx - eyeOffset, y: cy - eyeOffset };
        rightEye = { x: cx - eyeOffset, y: cy + eyeOffset };
        break;
      case 'right':
        leftEye = { x: cx + eyeOffset, y: cy - eyeOffset };
        rightEye = { x: cx + eyeOffset, y: cy + eyeOffset };
        break;
    }

    // White of eyes
    this.ctx.fillStyle = '#fff';
    this.ctx.beginPath();
    this.ctx.arc(leftEye.x, leftEye.y, eyeRadius, 0, Math.PI * 2);
    this.ctx.arc(rightEye.x, rightEye.y, eyeRadius, 0, Math.PI * 2);
    this.ctx.fill();

    // Pupils
    this.ctx.fillStyle = '#000';
    this.ctx.beginPath();
    this.ctx.arc(leftEye.x, leftEye.y, eyeRadius * 0.5, 0, Math.PI * 2);
    this.ctx.arc(rightEye.x, rightEye.y, eyeRadius * 0.5, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private drawBubble(bubble: Bubble): void {
    const x = bubble.x * this.cellWidth + this.cellWidth / 2;
    const y = bubble.y * this.cellHeight + this.cellHeight / 2;
    const radius = Math.min(this.cellWidth, this.cellHeight) * 0.35;

    // Rainbow konami bubbles get special treatment
    if (bubble.type === 'konami') {
      this.drawRainbowBubble(x, y, radius);
      return;
    }

    const color = BUBBLE_COLORS[bubble.type] || '#ffffff';

    // Glow effect
    const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, radius * 1.5);
    gradient.addColorStop(0, color);
    gradient.addColorStop(0.5, color + '80');
    gradient.addColorStop(1, 'transparent');

    this.ctx.beginPath();
    this.ctx.arc(x, y, radius * 1.5, 0, Math.PI * 2);
    this.ctx.fillStyle = gradient;
    this.ctx.fill();

    // Core
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.fillStyle = color;
    this.ctx.fill();

    // Shine
    this.ctx.beginPath();
    this.ctx.arc(x - radius * 0.3, y - radius * 0.3, radius * 0.2, 0, Math.PI * 2);
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    this.ctx.fill();
  }

  private drawRainbowBubble(x: number, y: number, radius: number): void {
    const time = Date.now() / 200;
    const hue = (time * 30) % 360;
    const pulseScale = 1 + 0.1 * Math.sin(Date.now() / 150);

    // Outer rainbow glow ring
    this.ctx.shadowColor = `hsl(${hue}, 100%, 60%)`;
    this.ctx.shadowBlur = 20;

    for (let i = 0; i < 6; i++) {
      const segHue = (hue + i * 60) % 360;
      const startAngle = (i / 6) * Math.PI * 2 + time * 0.5;
      const endAngle = ((i + 1) / 6) * Math.PI * 2 + time * 0.5;
      this.ctx.beginPath();
      this.ctx.arc(x, y, radius * 1.3 * pulseScale, startAngle, endAngle);
      this.ctx.lineWidth = radius * 0.4;
      this.ctx.strokeStyle = `hsl(${segHue}, 100%, 55%)`;
      this.ctx.stroke();
    }

    this.ctx.shadowBlur = 0;

    // Filled core — same size as normal bubbles
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius * pulseScale, 0, Math.PI * 2);
    const coreGrad = this.ctx.createRadialGradient(x, y, 0, x, y, radius * pulseScale);
    coreGrad.addColorStop(0, '#fff');
    coreGrad.addColorStop(0.4, `hsla(${hue}, 100%, 75%, 0.95)`);
    coreGrad.addColorStop(1, `hsla(${(hue + 180) % 360}, 100%, 55%, 0.9)`);
    this.ctx.fillStyle = coreGrad;
    this.ctx.fill();

    // Shine
    this.ctx.beginPath();
    this.ctx.arc(x - radius * 0.3, y - radius * 0.3, radius * 0.2, 0, Math.PI * 2);
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    this.ctx.fill();
  }

  private drawProjectile(projectile: Projectile): void {
    const x = projectile.x * this.cellWidth + this.cellWidth / 2;
    const y = projectile.y * this.cellHeight + this.cellHeight / 2;
    const radius = Math.min(this.cellWidth, this.cellHeight) * 0.2;

    // Trail
    const trailLength = 3;
    for (let i = 0; i < trailLength; i++) {
      const trailX = x - projectile.dx * this.cellWidth * (i * 0.3);
      const trailY = y - projectile.dy * this.cellHeight * (i * 0.3);
      const alpha = 1 - (i / trailLength);
      
      this.ctx.beginPath();
      this.ctx.arc(trailX, trailY, radius * (1 - i * 0.2), 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(255, 255, 0, ${alpha})`;
      this.ctx.fill();
    }

    // Main projectile
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.fillStyle = '#ffeb3b';
    this.ctx.fill();
  }

  private drawEgg(egg: SnakeEgg): void {
    const cx = (egg.x + 1) * this.cellWidth; // Center of 2x2 block
    const cy = (egg.y + 1) * this.cellHeight;
    const radiusX = this.cellWidth * 0.9;
    const radiusY = this.cellHeight * 1.1;
    const bobOffset = Math.sin(Date.now() / 400) * 1.5;

    // Wobble when about to hatch (last 5 seconds)
    const timeToHatch = egg.hatchAtMs - Date.now();
    let wobble = 0;
    if (timeToHatch < 5000 && timeToHatch > 0) {
      wobble = Math.sin(Date.now() / 80) * 3 * (1 - timeToHatch / 5000);
    }

    this.ctx.save();
    this.ctx.translate(cx + wobble, cy + bobOffset);

    // Egg glow
    this.ctx.shadowColor = '#8BC34A';
    this.ctx.shadowBlur = 10;

    // Main egg shape (ellipse)
    this.ctx.beginPath();
    this.ctx.ellipse(0, 0, radiusX, radiusY, 0, 0, Math.PI * 2);

    // Color based on HP (gets redder as damaged)
    const hpRatio = egg.hp / 3;
    const r = Math.floor(255 - hpRatio * 100);
    const g = Math.floor(200 * hpRatio + 120 * (1 - hpRatio));
    const b = Math.floor(100 * hpRatio);
    this.ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    this.ctx.fill();
    this.ctx.shadowBlur = 0;

    // Speckles
    const speckleColor = `rgba(80, 120, 40, ${0.3 + 0.3 * hpRatio})`;
    for (let i = 0; i < 6; i++) {
      const sx = Math.cos(i * 1.05 + 0.5) * radiusX * 0.5;
      const sy = Math.sin(i * 1.05 + 0.5) * radiusY * 0.5;
      this.ctx.beginPath();
      this.ctx.arc(sx, sy, this.cellWidth * 0.12, 0, Math.PI * 2);
      this.ctx.fillStyle = speckleColor;
      this.ctx.fill();
    }

    // Crack lines when damaged
    if (egg.hp < 3) {
      this.ctx.strokeStyle = '#333';
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      this.ctx.moveTo(-radiusX * 0.3, -radiusY * 0.2);
      this.ctx.lineTo(0, radiusY * 0.1);
      this.ctx.lineTo(radiusX * 0.2, -radiusY * 0.15);
      this.ctx.stroke();
    }
    if (egg.hp < 2) {
      this.ctx.beginPath();
      this.ctx.moveTo(radiusX * 0.1, radiusY * 0.2);
      this.ctx.lineTo(-radiusX * 0.2, radiusY * 0.4);
      this.ctx.lineTo(radiusX * 0.3, radiusY * 0.3);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  private drawHeadstone(headstone: Headstone): void {
    const cx = headstone.x * this.cellWidth + this.cellWidth / 2;
    const cy = headstone.y * this.cellHeight + this.cellHeight / 2;
    const w = this.cellWidth * 1.4;
    const h = this.cellHeight * 1.8;
    const bobOffset = Math.sin(Date.now() / 600) * 1.5;

    this.ctx.save();
    this.ctx.translate(cx, cy + bobOffset);

    // Glow
    this.ctx.shadowColor = '#aaa';
    this.ctx.shadowBlur = 10;

    // Tombstone body (rounded top rectangle)
    this.ctx.beginPath();
    this.ctx.moveTo(-w / 2, h * 0.1);
    this.ctx.lineTo(-w / 2, h / 2);
    this.ctx.lineTo(w / 2, h / 2);
    this.ctx.lineTo(w / 2, h * 0.1);
    // Rounded top
    this.ctx.arc(0, h * 0.1, w / 2, 0, Math.PI, true);
    this.ctx.closePath();

    // Stone gradient
    const grad = this.ctx.createLinearGradient(0, -h / 2, 0, h / 2);
    grad.addColorStop(0, '#888');
    grad.addColorStop(1, '#555');
    this.ctx.fillStyle = grad;
    this.ctx.fill();
    this.ctx.strokeStyle = '#333';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
    this.ctx.shadowBlur = 0;

    // RIP text
    this.ctx.fillStyle = '#222';
    this.ctx.font = `bold ${Math.max(8, this.cellHeight * 0.35)}px serif`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('RIP', 0, -h * 0.05);

    // Player name (small)
    this.ctx.fillStyle = '#333';
    this.ctx.font = `${Math.max(6, this.cellHeight * 0.25)}px sans-serif`;
    const name = headstone.playerName.length > 8
      ? headstone.playerName.slice(0, 7) + '…'
      : headstone.playerName;
    this.ctx.fillText(name, 0, h * 0.2);

    this.ctx.restore();
  }

  private drawRainbowSnake(snake: SnakeState): void {
    if (!snake.alive || snake.segments.length === 0) return;
    const segments = snake.segments;
    const time = Date.now() / 200;

    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i];
      const isHead = i === 0;
      const hue = ((i * 25) + time * 10) % 360;
      const color = `hsl(${hue}, 100%, 55%)`;

      const x = segment.x * this.cellWidth;
      const y = segment.y * this.cellHeight;
      const baseRadius = Math.min(this.cellWidth, this.cellHeight) * 0.45;
      const radius = isHead ? baseRadius * 1.15 : baseRadius;

      this.ctx.shadowColor = color;
      this.ctx.shadowBlur = isHead ? 15 : 8;

      this.ctx.beginPath();
      this.ctx.arc(x + this.cellWidth / 2, y + this.cellHeight / 2, radius, 0, Math.PI * 2);

      const alpha = 0.6 + (0.4 * (segments.length - i) / segments.length);
      this.ctx.fillStyle = color;
      this.ctx.globalAlpha = alpha;
      this.ctx.fill();
      this.ctx.globalAlpha = 1;
      this.ctx.shadowBlur = 0;

      if (isHead) {
        this.drawEyes(segment.x, segment.y, snake.direction);
      }
    }
  }

  private drawPlayerLabel(snake: SnakeState, name: string, color: string = '#9147ff'): void {
    if (snake.segments.length === 0) return;

    const head = snake.segments[0];
    
    // Position label above the snake's head with fixed offset
    const x = head.x * this.cellWidth + this.cellWidth / 2;
    const y = head.y * this.cellHeight - 30; // 30px above head

    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.font = 'bold 12px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    const textWidth = this.ctx.measureText(name).width;
    this.ctx.fillRect(x - textWidth / 2 - 4, y - 8, textWidth + 8, 16);

    this.ctx.fillStyle = color;
    this.ctx.fillText(name, x, y);
  }
}
